import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const PersonalBelongingList = ({
    belongings = [],
    onAddBelonging,
    onEditBelonging,
    onDeleteBelonging,
}) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // ✅ Filter Logic
    const filteredRecords = belongings.filter((r) =>
        Object.values(r).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // ✅ Custom Pagination (copied from InjectionList)
    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // ✅ Export PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Personal Belongings", 14, 10);
        const tableColumn = [
            "#",
            "Client",
            "Caregiver",
            "Relation",
            "Relative Name",
            "Created By",
            "Updated By",
        ];
        const tableRows = filteredRecords.map((r, index) => [
            index + 1,
            r.client_name,
            r.caregiver_name,
            r.relation,
            r.relative_name,
            r.created_by,
            r.updated_by,
        ]);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("personal_belongings.pdf");
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        { name: "Client", selector: (row) => row.client_name, sortable: true },
        { name: "Caregiver", selector: (row) => row.caregiver_name, sortable: true },
        { name: "Relation", selector: (row) => row.relation, sortable: true },
        { name: "Relative Name", selector: (row) => row.relative_name, sortable: true },
        { name: "Created By", selector: (row) => row.creator?.name || "-", sortable: true },
        { name: "Updated By", selector: (row) => row.updater?.name || "-", sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditBelonging && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit"
                            onClick={() => onEditBelonging(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteBelonging && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => onDeleteBelonging(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "130px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            {/* Header */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="personal_belongings.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search belongings..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    {onAddBelonging && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add New"
                            onClick={onAddBelonging}
                        >
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted">No belongings found</div>}
                />
            </div>
        </div>
    );
};

export default PersonalBelongingList;
